import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { updateWebForm } from 'src/shared/services/webFormService';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import ControlledRadioButtonGroup from 'src/shared/components/form/controlledRadioButtonGroup';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { WebForm } from '../models/webForm';

interface WebFormSettingsEditable {
  skipPaymentStep: boolean;
  duplicateCheckByCustomer: string;
  ignoreDuplicates: boolean;
  updateRequest: boolean;
  mailToSender: boolean;
  mailToLocal: boolean;
  subjectSender: string;
  welcomeURL: string;
  bodySender: string;
}

type WebFormDetailContext = {
  webForm: WebForm | undefined;
  fetchData: () => Promise<void>;
};

export const WebFormsDetailSettingsPage = () => {
  const { webForm, fetchData } = useOutletContext<WebFormDetailContext>();
  const isMounted = useIsMounted();
  const { t } = useTranslation(['webforms', 'common']);

  const [loading, setLoading] = useState<boolean>(false);

  const webFormSchema = yup.object().shape({
    skipPaymentStep: yup.boolean().required(),
    duplicateCheckByCustomer: yup.string().required(),
    ignoreDuplicates: yup.boolean().required(),
    updateRequest: yup.boolean().required(),
    mailToSender: yup.boolean().required(),
    mailToLocal: yup.boolean().required(),
    subjectSender: yup.string().default(''),
    welcomeURL: yup.string().default(''),
    bodySender: yup.string().default(''),
  });

  const formMethods = useForm<WebFormSettingsEditable>({
    resolver: yupResolver(webFormSchema),
    mode: 'all',
    defaultValues: {
      skipPaymentStep: false,
      duplicateCheckByCustomer: 'association',
      ignoreDuplicates: false,
      updateRequest: false,
      mailToSender: false,
      mailToLocal: false,
      subjectSender: '',
      welcomeURL: '',
      bodySender: '',
    },
  });

  const { handleSubmit, reset, watch } = formMethods;
  const ignoreDuplicates = watch('ignoreDuplicates');

  useEffect(() => {
    if (webForm) {
      const getSetting = (key: string) => webForm.settings.find((s) => s.keyName === key);
      const duplicateByCustomer = getSetting('duplicateCheckByCustomer')?.bit === true;

      reset({
        skipPaymentStep: getSetting('skipPaymentStep')?.bit === true,
        duplicateCheckByCustomer: duplicateByCustomer ? 'customer' : 'association',
        ignoreDuplicates: getSetting('ignoreDuplicates')?.bit === true,
        updateRequest: getSetting('updateRequest')?.bit === true,
        mailToSender: getSetting('mailToSender')?.bit === true,
        mailToLocal: getSetting('mailToLocal')?.bit === true,
        subjectSender: getSetting('subjectSender')?.varchar || '',
        welcomeURL: getSetting('welcomeURL')?.varchar || '',
        bodySender: getSetting('bodySender')?.text || '',
      });
    }
  }, [webForm, reset]);

  const mailToSender = watch('mailToSender');

  const onSubmit = useCallback(
    async (data: WebFormSettingsEditable) => {
      if (!webForm) return;

      const updatedSettings = webForm.settings.map((s) => {
        switch (s.keyName) {
          case 'skipPaymentStep':
            return { ...s, bit: data.skipPaymentStep };
          case 'duplicateCheckByCustomer':
            return { ...s, bit: data.duplicateCheckByCustomer === 'customer' };
          case 'ignoreDuplicates':
            return { ...s, bit: data.ignoreDuplicates };
          case 'updateRequest':
            return { ...s, bit: data.updateRequest };
          case 'mailToSender':
            return { ...s, bit: data.mailToSender };
          case 'mailToLocal':
            return { ...s, bit: data.mailToLocal };
          case 'subjectSender':
            return { ...s, varchar: data.subjectSender };
          case 'welcomeURL':
            return { ...s, varchar: data.welcomeURL };
          case 'bodySender':
            return { ...s, text: data.bodySender };
          default:
            return s;
        }
      });

      const updatedWebForm: WebForm = {
        ...webForm,
        settings: updatedSettings,
      };

      try {
        setLoading(true);
        await updateWebForm(updatedWebForm);
        await fetchData();
      } catch (error) {
        ConsoleHelper.log(error);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [fetchData, isMounted, webForm],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLoaderWrapper loading={loading}>
          <Grid container sx={{ p: 3 }}>
            {/* Betalning */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {t('webforms:settingsPage.paymentTitle')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {t('webforms:settingsPage.paymentDescription')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <ControlledCheckboxNew name='skipPaymentStep' label={t('webforms:settingsPage.skipPaymentStep')} />
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
            </Grid>

            {/* Dubblettkontroll */}
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {t('webforms:settingsPage.duplicateCheckTitle')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {t('webforms:settingsPage.duplicateCheckDescription')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <ControlledRadioButtonGroup
                  name='duplicateCheckByCustomer'
                  items={[
                    { value: 'customer', label: t('webforms:settingsPage.duplicateCheckByCustomer') },
                    { value: 'association', label: t('webforms:settingsPage.duplicateCheckByAssociation') },
                  ]}
                  disabled={ignoreDuplicates}
                />
                <ControlledCheckboxNew name='ignoreDuplicates' label={t('webforms:settingsPage.ignoreDuplicates')} />
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
            </Grid>

            {/* Efter inskickat formulär */}
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {t('webforms:settingsPage.afterSubmitTitle')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {t('webforms:settingsPage.afterSubmitDescription')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8} sx={{ mt: { xs: 0, md: 2 } }}>
                <ControlledTextField
                  fullWidth
                  label={t('webforms:settingsPage.welcomeURL')}
                  placeholder='https://'
                  size='small'
                  name='welcomeURL'
                  sx={{ mb: 1 }}
                />
                <ControlledCheckboxNew name='updateRequest' label={t('webforms:settingsPage.updateRequest')} />
                <Box>
                  <ControlledCheckboxNew name='mailToSender' label={t('webforms:settingsPage.mailToSender')} />
                </Box>
                {mailToSender && (
                  <>
                    <ControlledTextField
                      fullWidth
                      label={t('webforms:settingsPage.subjectSender')}
                      size='small'
                      name='subjectSender'
                      sx={{ mt: 1, mb: 1 }}
                    />
                    <ControlledTextEditor name='bodySender' hidedataPropFields />
                  </>
                )}
                <ControlledCheckboxNew name='mailToLocal' label={t('webforms:settingsPage.mailToLocal')} />
              </Grid>
            </Grid>

            {/* Spara */}
            <Grid item xs={12} display='flex' justifyContent='flex-end' sx={{ mt: 1 }}>
              <Button size='medium' variant='contained' disabled={loading} type='submit'>
                {t('common:button.save')}
              </Button>
            </Grid>
          </Grid>
        </FormLoaderWrapper>
      </form>
    </FormProvider>
  );
};
